import * as React from "react"
import { ModuleLoading } from "@digits-shared/components/Loaders"
import { LoggedRedirect } from "@digits-shared/components/Router/LoggedRedirect"
import { useEffectOnce } from "@digits-shared/hooks/useEffectOnce"
import { AnimatePresence } from "framer-motion"
import { EmailSignIn } from "src/frontend/components/Public/LogIn/Glow/EmailSignIn"
import { ForgotPassword } from "src/frontend/components/Public/LogIn/Glow/ForgotPassword"
import { MagicLinkExpired } from "src/frontend/components/Public/LogIn/Glow/MagicLinkExpired"
import { MagicLinkLoading } from "src/frontend/components/Public/LogIn/Glow/MagicLinkLoading"
import { MagicLinkSent } from "src/frontend/components/Public/LogIn/Glow/MagicLinkSent"
import { PasswordResetSent } from "src/frontend/components/Public/LogIn/Glow/PasswordResetSent"
import { SignIn } from "src/frontend/components/Public/LogIn/Glow/SignIn"
import {
  OnboardingStateProvider,
  useOnboardingContext,
} from "src/frontend/components/Public/Onboarding/OnboardingContext"
import { OnboardingStep } from "src/frontend/components/Public/Onboarding/reducer"
import { useInitialLoginState } from "src/frontend/components/Session/useInitialLoginState"
import { slide, type TransitionCustom } from "src/frontend/components/Shared/Glow/animations"
import { AnimatedWrapper, Biscuit } from "src/frontend/components/Shared/Glow/Biscuit"
import routes from "src/frontend/routes"

/*
  INTERFACES
 */

type AnimatedStep =
  | OnboardingStep.SignIn
  | OnboardingStep.EmailSignIn
  | OnboardingStep.ForgotPassword
  | OnboardingStep.PasswordResetSent
  | OnboardingStep.MagicLinkSent
  | OnboardingStep.MagicLinkExpired

/*
  COMPONENTS
*/

export const LogInScreens: React.FC = () => {
  const { initialState, emailAddress, invitationToken, redirectPath } = useInitialLoginState()
  return (
    <OnboardingStateProvider
      initialState={initialState}
      emailAddress={emailAddress}
      invitationToken={invitationToken}
      redirectPath={redirectPath}
    >
      <LogInSteps />
    </OnboardingStateProvider>
  )
}

const LogInSteps: React.FC = () => {
  const {
    state: { step },
  } = useOnboardingContext()
  return isStepAnimated(step) ? <AnimatedSteps step={step} /> : <BareStepRenderer step={step} />
}

/**
 * Render the step with a slide transition wrapped in a biscuit.
 */
const AnimatedSteps: React.FC<{ step: AnimatedStep }> = ({ step }) => {
  const custom = useCustomAnimation()

  return (
    // we have to use mode "wait" in order for password managers to handle automatic form submission from the first page
    <AnimatePresence mode="wait" custom={custom}>
      <AnimatedWrapper
        key={step}
        variants={slide}
        custom={custom}
        initial="initial"
        animate="animate"
        exit="exit"
      >
        <Biscuit>
          <AnimatableStepRenderer step={step} />
        </Biscuit>
      </AnimatedWrapper>
    </AnimatePresence>
  )
}

const AnimatableStepRenderer: React.FC<{ step: AnimatedStep }> = ({ step }) => {
  switch (step) {
    case OnboardingStep.SignIn:
      return <SignIn />
    case OnboardingStep.EmailSignIn:
      return <EmailSignIn />
    case OnboardingStep.ForgotPassword:
      return <ForgotPassword />
    case OnboardingStep.PasswordResetSent:
      return <PasswordResetSent />
    case OnboardingStep.MagicLinkSent:
      return <MagicLinkSent />
    case OnboardingStep.MagicLinkExpired:
      return <MagicLinkExpired />
    default:
      return null
  }
}

/**
 * Renders a step without any style wrappers.
 */
const BareStepRenderer: React.FC<{ step: OnboardingStep }> = ({ step }) => {
  switch (step) {
    case OnboardingStep.MagicLinkLoading:
      return <MagicLinkLoading />
    case OnboardingStep.None:
      return <ModuleLoading />
    default:
      return <UnexpectedLoginStep step={step} />
  }
}

const UnexpectedLoginStep: React.FC<{ step: OnboardingStep }> = ({ step }) => {
  useEffectOnce(() => {
    TrackJS?.track(`Unexpected login step: ${step}`)
  })

  return <LoggedRedirect to={routes.root.generate()} name="log-in-unexpected-redirect" />
}

function isStepAnimated(step: OnboardingStep): step is AnimatedStep {
  switch (step) {
    case OnboardingStep.SignIn:
    case OnboardingStep.EmailSignIn:
    case OnboardingStep.ForgotPassword:
    case OnboardingStep.PasswordResetSent:
    case OnboardingStep.MagicLinkSent:
    case OnboardingStep.MagicLinkExpired:
      return true
    default:
      return false
  }
}

function useCustomAnimation() {
  const {
    state: { back: reverse },
  } = useOnboardingContext()
  return React.useMemo(() => ({ reverse, size: "sm" }) satisfies TransitionCustom, [reverse])
}
